import { useDateNow, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Badge } from 'components/common';
import { BadgeCategory } from 'components/common/Badge';

import type { CalendarEventExtended } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  event: CalendarEventExtended;
}

const CalendarEventStatus = ({ event }: Props) => {
  const { dateNow } = useDateNow();

  const { messages } = useReduxState();

  let eventStatus = 'available';
  if (event.userIsOwner) {
    eventStatus = 'owner';
  } else if (event.userStatus === 'added') {
    eventStatus = 'added';
  } else if (event.userStatus === 'queued') {
    eventStatus = event.access === 'request-access' ? 'requested' : 'queued';
  } else if (Date.parse(event.endDate) < dateNow) {
    eventStatus = 'ended';
  } else if (Date.parse(event.startDate) < dateNow) {
    eventStatus = 'started';
  }

  return (
    <Badge
      aria-label={messages.commonStatus}
      category={
        ({
          added: 'success-light',
          owner: 'success-light',
          queued: 'warning-light',
          requested: 'warning-light'
        }[eventStatus] || 'default') as BadgeCategory
      }
      icon={
        {
          owner: 'key' as const,
          queued: ['far', 'clock'] as IconProp,
          requested: ['far', 'clock'] as IconProp
        }[eventStatus]
      }
      isRounded={false}
      text={
        {
          added: messages.commonAdded,
          available: messages.commonAvailable,
          ended: messages.publishEnded,
          owner: messages.commonOwner,
          queued: getMessageWithPlaceholders({
            placeholders: { queueSpot: event.participantQueueSpot },
            message: messages.eventCalendarInQueue
          }),
          requested: messages.commonAccessRequested,
          started: messages.commonInProgress
        }[eventStatus]
      }
    />
  );
};

export default CalendarEventStatus;
